import { FC } from 'react';
import { HoldingClass, Partner, TradingOffer } from 'types';
import { TradingOfferStatus } from 'types/TradingOffer';
import {
  formatCurrency,
  formatCurrencyDecimals,
  formatNumber,
} from 'utils/formats';
import { useTranslate } from 'hooks/useTranslate';
import { isShareClassVisible } from 'modules/trading/utils/isShareClassVisible';
import TradingOfferStatusBadge from '../../../components/TradingOfferStatusBadge';

type EquityMarketAdminPanelOfferItemProps = {
  offer: TradingOffer;
  isSell: boolean;
  onViewDetail: (id: string) => void; // eslint-disable-line no-unused-vars
  getOfferStatus: (o: TradingOffer) => TradingOfferStatus; // eslint-disable-line no-unused-vars
  getPartnerName: (p: string | Partial<Partner>) => string; // eslint-disable-line no-unused-vars
  getShareClassName: (s: string | Partial<HoldingClass> | null) => string; // eslint-disable-line no-unused-vars
};

const EquityMarketAdminPanelOfferItem: FC<
  EquityMarketAdminPanelOfferItemProps
> = ({
  offer,
  isSell,
  onViewDetail,
  getOfferStatus,
  getPartnerName,
  getShareClassName,
}) => {
  const { t } = useTranslate();

  const status = getOfferStatus(offer);
  const partnerName = getPartnerName(offer.partner);
  const shareClassName = getShareClassName(offer.shareClass);
  const totalAmount = offer.sharesCount.total * offer.sharePrice;
  const iconBg = isSell
    ? 'bg-success-dim text-success'
    : 'bg-info-dim text-info';
  const iconName = isSell ? 'ni-wallet-out' : 'ni-wallet-in';

  const formatOfferDescription = () => {
    const priceBlock = `${formatCurrencyDecimals(offer.sharePrice)}/${t(
      'Share'
    ).toLowerCase()}`;
    const totalBlock = `${t('Total')}: ${formatCurrency(totalAmount)}`;
    const partnerBlock = `${isSell ? t('Seller') : t('Buyer')}: ${partnerName}`;

    return `${priceBlock} · ${totalBlock} · ${partnerBlock}`;
  };

  const formatOfferSummary = () => {
    const sharesBlock = `${formatNumber(offer.sharesCount.total)} ${t(
      'Shares'
    ).toLowerCase()}`;
    const shareClassNameBlock = isShareClassVisible()
      ? `· ${shareClassName}`
      : '';

    return `${sharesBlock} ${shareClassNameBlock}`;
  };

  return (
    <div
      className="col-12 list-item-oferta doc-item d-flex align-items-center cursor-pointer"
      onClick={() => onViewDetail(offer._id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onViewDetail(offer._id)}
    >
      <div className={`doc-icon ${iconBg}`}>
        <em className={`icon ni ${iconName}`} />
      </div>

      <div className="flex-grow-1">
        <strong className="fs-13px">{formatOfferSummary()}</strong>
        <div className="text-soft fs-12px">{formatOfferDescription()}</div>
      </div>

      <div className="d-flex align-items-center gap-2">
        <TradingOfferStatusBadge status={status} />

        <a
          className="btn btn-icon btn-round btn-trans"
          href="#"
          title={t('ViewDetail')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onViewDetail(offer._id);
          }}
        >
          <em className="icon ni ni-forward-ios" />
        </a>
      </div>
    </div>
  );
};

export default EquityMarketAdminPanelOfferItem;
